const STORAGE_PREFIX = 'acompanha:printer-snapshot:';
const TEST_TARGET_KEY = 'acompanha:printer-snapshot-test-target';

/**
 * Depois disso a foto não representa mais o estado da impressora, então é melhor
 * mostrar o placeholder do que uma imagem defasada.
 */
export const PRINTER_SNAPSHOT_MAX_AGE_MS = 60 * 60 * 1000;

export function resolvePrinterUuid(printer) {
    return String(printer?.printer_uuid || printer?.id || printer?.uuid || '').trim();
}

/** Sem data confiável tratamos como expirado: não há como saber se envelheceu. */
export function isPrinterSnapshotExpired(updatedAt, now = Date.now()) {
    const timestamp = Date.parse(String(updatedAt || ''));
    if (Number.isNaN(timestamp)) {
        return true;
    }
    return now - timestamp >= PRINTER_SNAPSHOT_MAX_AGE_MS;
}

export function loadPrinterSnapshot(printerUuid) {
    if (!printerUuid) {
        return null;
    }
    const storageKey = `${STORAGE_PREFIX}${printerUuid}`;
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        const dataUrl = String(parsed?.dataUrl || '').trim();
        if (!dataUrl) {
            return null;
        }
        const updatedAt = String(parsed?.updatedAt || '').trim() || null;
        if (isPrinterSnapshotExpired(updatedAt)) {
            localStorage.removeItem(storageKey);
            return null;
        }
        return { dataUrl, updatedAt };
    } catch {
        return null;
    }
}

export function loadPrinterSnapshotDataUrl(printerUuid) {
    return loadPrinterSnapshot(printerUuid)?.dataUrl || null;
}

export function persistPrinterSnapshotDataUrl(
    printerUuid,
    dataUrl,
    updatedAt = new Date().toISOString(),
) {
    if (!printerUuid || !dataUrl) {
        return false;
    }
    try {
        localStorage.setItem(`${STORAGE_PREFIX}${printerUuid}`, JSON.stringify({
            dataUrl,
            updatedAt,
        }));
        return true;
    } catch (error) {
        console.warn('[printer-snapshot] falha ao salvar no localStorage', error);
        return false;
    }
}

export function bytesToDataUrl(bytes, contentType = 'image/jpeg') {
    if (!bytes || !bytes.length) {
        return null;
    }
    const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const chunkSize = 0x8000;
    let binary = '';
    for (let i = 0; i < view.length; i += chunkSize) {
        const slice = view.subarray(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, slice);
    }
    const mime = String(contentType || 'image/jpeg').trim() || 'image/jpeg';
    return `data:${mime};base64,${btoa(binary)}`;
}

/**
 * Limpa snapshots que não devem mais ser exibidos: os do antigo modo de teste e
 * os que passaram da validade. Roda na inicialização para alcançar também as
 * impressoras que não estão na tela, cujo snapshot ninguém iria ler para expirar.
 */
export function purgeObsoletePrinterSnapshotsFromStorage() {
    try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i);
            if (!key?.startsWith(STORAGE_PREFIX)) {
                continue;
            }
            try {
                const parsed = JSON.parse(localStorage.getItem(key));
                if (parsed?.isTest || isPrinterSnapshotExpired(parsed?.updatedAt)) {
                    keysToRemove.push(key);
                }
            } catch {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
        localStorage.removeItem(TEST_TARGET_KEY);
    } catch {
        // ignore quota or access errors
    }
}
